import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { AlertTriangle, Check, FileText, Loader2, Trash2, WifiOff, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DeletePiecesConfirmationVerdict } from '../../types/session.types';
import { orphanedFilesNotice } from '@/lib/orphanedFilesNotice';

export interface DeletePiecePreview {
  documentId: string;
  title: string;
  pieceNumber?: string;
}

interface DeletePiecesConfirmationCardProps {
  caseId: string;
  /** Titre du dossier, nommé sur la carte : une conversation peut en porter plusieurs. */
  caseTitle?: string;
  requestId: string;
  pieces: DeletePiecePreview[];
  result?: DeletePiecesConfirmationVerdict;
  onRespond?: (payload: { caseId: string; accepted: boolean; requestId: string }) => boolean;
}

type Decision = 'pending' | 'submitting' | 'succeeded' | 'dismissed' | 'expired';

/**
 * Inline consent card for deleting case source pieces.
 *
 * The card owns only the transient UI state. The definitive state comes from
 * the correlated backend result, never from a successful WebSocket send.
 */
export function DeletePiecesConfirmationCard({
  caseId,
  caseTitle,
  requestId,
  pieces,
  result,
  onRespond,
}: DeletePiecesConfirmationCardProps) {
  const fromCase = caseTitle ? `du dossier « ${caseTitle} »` : 'du dossier';
  const [decision, setDecision] = useState<Decision>('pending');
  const [submittedAction, setSubmittedAction] = useState<'delete' | 'cancel' | null>(null);
  const [sendFailed, setSendFailed] = useState(false);

  useEffect(() => {
    setDecision('pending');
    setSubmittedAction(null);
    setSendFailed(false);
  }, [requestId]);

  useEffect(() => {
    if (!result) return;
    if (result.deleted) {
      setDecision('succeeded');
    } else if (result.declined) {
      setDecision('dismissed');
    } else {
      setDecision('expired');
    }
  }, [requestId, result]);

  const respond = (accepted: boolean, action: 'delete' | 'cancel') => {
    if (decision !== 'pending' || !onRespond) return;
    // A sent frame acknowledges transport only. The pending state prevents a
    // duplicate click while the backend rechecks role and current document rows.
    if (onRespond({ caseId, accepted, requestId })) {
      setSubmittedAction(action);
      setDecision('submitting');
    } else {
      setSendFailed(true);
    }
  };

  if (decision === 'succeeded') {
    const s3Warning = (result?.s3ErrorCount ?? 0) > 0
      ? ` ${orphanedFilesNotice(result?.s3ErrorCount ?? 0)}`
      : '';
    return (
      <CompactStatus
        icon={<Check className="h-3.5 w-3.5 text-grey-400" />}
        label={`${result?.deletedCount ?? pieces.length} pièce${plural(result?.deletedCount ?? pieces.length)} supprimée${plural(result?.deletedCount ?? pieces.length)} ${fromCase}.${s3Warning}`}
      />
    );
  }
  if (decision === 'dismissed') {
    return <CompactStatus icon={<X className="h-3.5 w-3.5 text-grey-400" />} label="Suppression annulée. Aucune pièce n’a été supprimée." />;
  }
  if (decision === 'expired') {
    return (
      <CompactStatus
        icon={<AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
        label={result?.userMessage ?? (result?.timedOut
          ? 'La demande de suppression a expiré. Aucune pièce n’a été supprimée.'
          : 'La suppression n’a pas pu être finalisée. Aucune pièce supplémentaire n’a été supprimée.')}
      />
    );
  }
  if (decision === 'submitting') {
    return (
      <CompactStatus
        icon={<Loader2 className="h-3.5 w-3.5 animate-spin text-grey-400" />}
        label={submittedAction === 'cancel' ? 'Annulation en cours…' : 'Validation de la suppression en cours…'}
      />
    );
  }

  const count = pieces.length;
  const isInteractive = Boolean(onRespond);
  return (
    <section className="overflow-hidden rounded-lg border border-grey-200 bg-white" aria-label="Validation de suppression de pièces">
      <div className="flex items-center gap-2 border-b border-grey-100 bg-grey-50/60 px-3.5 py-2">
        <Trash2 className="h-3.5 w-3.5 flex-shrink-0 text-grey-400" />
        <span className="text-xs font-medium text-grey-600">Suppression de pièces</span>
      </div>
      <div className="space-y-3 px-3.5 py-3">
        <div>
          <h3 className="text-sm font-medium text-grey-800">Supprimer {count} pièce{plural(count)} {fromCase} ?</h3>
          <p className="mt-1 text-sm text-grey-700">
            Cette action retire définitivement {count > 1 ? 'ces pièces' : 'cette pièce'} et leurs données associées.
          </p>
        </div>
        <ul className="max-h-56 divide-y divide-grey-100 overflow-y-auto rounded-md border border-grey-200 bg-grey-50/70" aria-label="Pièces à supprimer">
          {pieces.map((piece) => (
            <li key={piece.documentId} className="flex items-start gap-2 px-3 py-2 text-sm text-grey-700">
              <FileText className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-grey-400" />
              <span className="min-w-0 break-words">
                {piece.pieceNumber ? <span className="font-medium text-grey-800">Pièce n°{piece.pieceNumber} : </span> : null}
                {piece.title}
              </span>
            </li>
          ))}
        </ul>
      </div>
      {sendFailed ? (
        <div className="flex items-center gap-1.5 border-t border-amber-200 bg-amber-50 px-3.5 py-2 text-xs text-amber-700">
          <WifiOff className="h-3.5 w-3.5 flex-shrink-0" />
          Connexion perdue, réessayez dans un instant.
        </div>
      ) : null}
      <div className="flex justify-end gap-2 border-t border-grey-100 px-3.5 py-2">
        <button
          type="button"
          onClick={() => respond(false, 'cancel')}
          disabled={!isInteractive}
          className="flex items-center gap-1.5 rounded-lg border border-grey-200 px-4 py-2 text-sm font-medium text-grey-600 transition hover:bg-grey-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <X className="h-3.5 w-3.5" />
          Annuler
        </button>
        <button
          type="button"
          onClick={() => respond(true, 'delete')}
          disabled={!isInteractive}
          className={cn(
            'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-150',
            'bg-red-600 text-white hover:bg-red-700 active:scale-[0.98]',
            'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-red-600',
          )}
        >
          <Trash2 className="h-4 w-4" />
          Supprimer {count} pièce{plural(count)}
        </button>
      </div>
    </section>
  );
}

function plural(count: number): string {
  return count > 1 ? 's' : '';
}

function CompactStatus({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <div role="status" aria-live="polite" className="overflow-hidden rounded-lg border border-grey-200 bg-white">
      <div className="flex items-start gap-2 px-3.5 py-2">
        <Trash2 className="mt-px h-3.5 w-3.5 flex-shrink-0 text-grey-400" />
        <span className="text-xs font-medium text-grey-600">Suppression de pièces</span>
        <span className="ml-auto inline-flex max-w-md items-start gap-1.5 text-right text-xs font-medium text-grey-500">
          {icon}
          {label}
        </span>
      </div>
    </div>
  );
}
